import { getCsrfToken } from "@/lib/csrf";
import type { RoutingHistoryFilters } from "@/types/admin-portal/routing-mode";

export type RoutingResult<T> =
	| { ok: true; data: T }
	| { ok: false; error: string };

/**
 * Sends a write to the Rails proxy, which forwards the backend status and message.
 * The backend's 400 text names the bad input (e.g. the unknown location_id in a bulk
 * list), so it is returned untouched for display.
 */
export async function sendRouting<T>(
	url: string,
	method: "PUT" | "DELETE",
	body: Record<string, unknown>,
	fallbackError: string,
): Promise<RoutingResult<T>> {
	try {
		const res = await fetch(url, {
			method,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-CSRF-Token": getCsrfToken(),
			},
			body: JSON.stringify(body),
		});
		const json = await res.json().catch(() => null);
		if (res.ok && json?.success) return { ok: true, data: json.data as T };
		return { ok: false, error: json?.error || fallbackError };
	} catch {
		return { ok: false, error: fallbackError };
	}
}

export function formatWib(iso?: string | null): string {
	if (!iso) return "—";
	return new Date(iso).toLocaleString("id-ID", {
		timeZone: "Asia/Jakarta",
		dateStyle: "medium",
		timeStyle: "short",
	});
}

/**
 * A calibration field: "" = null = the backend default (1.4 / 22.5). The table shows
 * "1,35", so a decimal comma is read as a point; anything else that is not a number
 * comes back NaN for the range check to reject. It must never become null, which the
 * backend reads as "clear the calibration".
 */
export function parseOptionalNumber(value: string): number | null {
	const trimmed = value.trim();
	return trimmed === "" ? null : Number(trimmed.replace(",", "."));
}

/** A stored calibration as the field shows it, with the decimal comma the table uses. */
export function decimalText(value: number | null): string {
	return value == null ? "" : String(value).replace(".", ",");
}

/** The backend's history page size when the URL names none. */
export const HISTORY_DEFAULT_LIMIT = 50;

/** The URL query for one page of history; defaults and blank fields stay out. */
export function historyQuery(
	filters: RoutingHistoryFilters,
	page: number,
	limit: number,
) {
	return {
		history: 1,
		location_id: filters.locationId,
		changed_by: filters.changedBy,
		from: filters.from || undefined,
		to: filters.to || undefined,
		page: page > 1 ? page : undefined,
		limit: limit === HISTORY_DEFAULT_LIMIT ? undefined : limit,
	};
}
